import React, { useState, useEffect, useMemo } from "react";
import "../css/Common.css";
import "../css/Accordion.css";
import "./css/Account.css";
import AccountAPI from "../services/AccountAPI";
import { getCookie } from "../utils/CookieUtil";
import { useUser } from "../context/UserContext";
import { useSubscription } from "../context/SubscriptionContext";
import { useEdition } from "../context/EditionContext";
import { useRoleCheck } from "../../../hooks/useRoleCheck";
import { useTranslation } from "../../../i18n/LanguageContext";
import { formatDateOnly } from "../utils/DateTimeUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faChevronRight,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../common/LoadingSpinner";

// Industry options for corporate accounts. Kept aligned with the registration
// flow (CreateAccountUser.js) so the cyber-insurance readiness report stays
// consistent regardless of where the value was set.
const INDUSTRY_OPTIONS = [
  "Professional Services",
  "Technology & Software",
  "Finance & Insurance",
  "Healthcare",
  "Retail & E-commerce",
  "Manufacturing",
  "Construction",
  "Education",
  "Legal",
  "Real Estate",
  "Hospitality",
  "Transportation & Logistics",
  "Nonprofit",
  "Government & Public Sector",
  "Other",
];

/**
 * AccountProfile — account-owner self-service hub that bundles three sections:
 *   1. Account        — read-only account identity / ownership / usage overview
 *   2. Company Profile — editable business demographics (corporate accounts only)
 *   3. User Profile    — the owner's own personal profile (reuses MemberUserInfo_v2)
 *
 * This page is only reachable from the "Account Profile" menu item, which is
 * gated to account owners/admins. Members keep their existing "User Profile"
 * menu entry, so this page never needs a member-only fallback.
 */
function AccountProfile({ setHeroContent }) {
  const { loggedInUser } = useUser();
  const { getMaxUsers, getPlanName } = useSubscription();
  const { isCorporate } = useEdition();
  const { isAccountAdmin } = useRoleCheck();
  const { t } = useTranslation();

  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Exclusive accordion: only one of "company" / "users" expanded at a time.
  const [expandedSection, setExpandedSection] = useState(null);

  // Authoritative corporate signal is the account's own account_type (loaded
  // from the account API). EditionContext.isCorporate is a heuristic derived
  // from the subscription plan / localStorage and can be unset for seeded or
  // freshly loaded sessions, so it is only a fallback before accountData arrives.
  const accountType =
    accountData?.accountType || accountData?.account_type || null;
  const isCorporateAccount = accountType
    ? String(accountType).toUpperCase() === "CORPORATE"
    : isCorporate;
  const isCompanyOwner = isCorporateAccount && isAccountAdmin();

  // ── Company profile editing state ────────────────────────────────────
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: "",
    description: "",
    industry: "",
    employeeCount: "",
  });
  const [companySaving, setCompanySaving] = useState(false);
  const [companySaveError, setCompanySaveError] = useState(null);
  const [companySaveSuccess, setCompanySaveSuccess] = useState(false);

  useEffect(() => {
    fetchAccountData();
  }, []);

  const fetchAccountData = async () => {
    try {
      setLoading(true);
      setError(null);

      const accountOwnerUuid = getCookie("uuid");
      if (!accountOwnerUuid) {
        // Mounted before UserContext set the uuid cookie; bail quietly and
        // let the global auth gate restore/refresh the session.
        setLoading(false);
        return;
      }

      const response = await AccountAPI.getAccountByUserUuid();
      const accountInfo = response.data || response;

      if (!accountInfo) {
        setError(t("accountProfile.account.notProvided"));
        return;
      }
      setAccountData(accountInfo);
    } catch (err) {
      console.error("AccountProfile: Error fetching account data:", err);
      setError("Failed to load account information. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // ── Section expand/collapse (exclusive accordion) ────────────────────
  const scrollSectionToTop = (sectionId) => {
    requestAnimationFrame(() => {
      document
        .getElementById(`account-section-${sectionId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const toggleSection = (sectionId) => {
    const willExpand = expandedSection !== sectionId;
    setExpandedSection(willExpand ? sectionId : null);
    // When opening a section, bring its header to the top of the page.
    if (willExpand) {
      scrollSectionToTop(sectionId);
    }
  };

  // Accordion section renderer (same pattern as the Subscription & Payment page)
  const renderAccordion = ({
    id,
    icon,
    iconClass,
    title,
    subtitle,
    expanded,
    onToggle,
    children,
  }) => (
    <div className="settings-accordion" id={`account-section-${id}`}>
      <div className="settings-accordion-header" onClick={onToggle}>
        <div className={`accordion-icon ${iconClass}`}>
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="accordion-title-group">
          <h3 className="accordion-title">{title}</h3>
          <p className="accordion-subtitle">{subtitle}</p>
        </div>
        <FontAwesomeIcon
          icon={faChevronRight}
          className={`accordion-chevron ${expanded ? "expanded" : ""}`}
        />
      </div>
      <div
        className={`settings-accordion-content ${expanded ? "expanded" : ""}`}
        style={expanded ? undefined : { display: "none" }}
      >
        {children}
      </div>
    </div>
  );

  // ── Company profile editing handlers ─────────────────────────────────
  const beginEditCompany = () => {
    setCompanyForm({
      name: accountData?.name || "",
      description: accountData?.description || "",
      industry: accountData?.industry || "",
      employeeCount:
        accountData?.employeeCount != null
          ? String(accountData.employeeCount)
          : "",
    });
    setCompanySaveError(null);
    setCompanySaveSuccess(false);
    setIsEditingCompany(true);
    // Keep the company-profile header pinned to the top when the edit form
    // (which is taller) replaces the read-only view.
    scrollSectionToTop("company");
  };

  const cancelEditCompany = () => {
    setIsEditingCompany(false);
    setCompanySaveError(null);
  };

  const handleCompanyFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCompanyForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCompanySave = async (e) => {
    e.preventDefault();
    setCompanySaveError(null);
    setCompanySaveSuccess(false);

    const trimmedName = companyForm.name.trim();
    if (!trimmedName) {
      setCompanySaveError(t("company.profile.errorNameRequired"));
      return;
    }

    let employeeCount = null;
    if (companyForm.employeeCount !== "") {
      employeeCount = parseInt(companyForm.employeeCount, 10);
      if (Number.isNaN(employeeCount) || employeeCount < 1) {
        setCompanySaveError(t("company.profile.errorEmployeeCount"));
        return;
      }
    }

    try {
      setCompanySaving(true);
      const payload = {
        name: trimmedName,
        description: companyForm.description.trim(),
        industry: companyForm.industry,
        employeeCount,
      };

      const response = await AccountAPI.updateCompanyProfile(payload);
      const updated = response?.data || response;

      if (updated) {
        setAccountData(updated);
      }
      setIsEditingCompany(false);
      setCompanySaveSuccess(true);
    } catch (err) {
      console.error("AccountProfile: Error updating company profile:", err);
      setCompanySaveError(
        err?.response?.data?.message || t("company.profile.errorSave"),
      );
    } finally {
      setCompanySaving(false);
    }
  };

  // ── Section renderers ────────────────────────────────────────────────
  const notProvided = t("accountProfile.account.notProvided");

  const ownerName =
    [loggedInUser?.firstName, loggedInUser?.lastName]
      .filter(Boolean)
      .join(" ") || t("accountProfile.account.ownerYou");
  const ownerEmail = loggedInUser?.email || "";

  const isActive = accountData?.isActive ?? accountData?.is_active;
  const isDelinquent = accountData?.isDelinquent ?? accountData?.is_delinquent;
  const createdAt = accountData?.createdAt || accountData?.created_at;
  const seatsUsed = accountData?.userCount ?? 0;
  const seatsTotal = getMaxUsers() || 0;

  // Decorative display ID:
  //   <CompanyInitial>-<FirstAlnum><LastAlnum><accountId>-<MemberSinceDigits>
  // e.g. "R-RC44-06262026" for "Reliable IT Associates, LLC." joined
  // June 26, 2026 with account id 44. The alphanumeric characters ignore
  // punctuation/whitespace and the date is the Member Since date with all
  // digits mashed together (MMDDYYYY).
  const rawAccountId =
    accountData?.accountId ?? accountData?.account_id ?? null;
  const displayAccountId = useMemo(() => {
    if (rawAccountId == null) return notProvided;

    const name = (accountData?.name || "").trim();
    const initial = (name.charAt(0) || "?").toUpperCase();

    const alnum = name.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    const firstAlnum = alnum.charAt(0) || "?";
    const lastAlnum = alnum.charAt(alnum.length - 1) || "?";

    const mashedDate = createdAt
      ? formatDateOnly(createdAt, "short").replace(/\D/g, "")
      : "";

    return `${initial}-${firstAlnum}${lastAlnum}${rawAccountId}-${mashedDate}`;
  }, [rawAccountId, accountData?.name, createdAt]);

  const renderAccountSection = () => (
    <>
      <div className="common-form-group">
        <div className="account-info-grid">
          <div className="info-item">
            <label>{t("accountProfile.account.accountName")}:</label>
            <span>{accountData?.name || notProvided}</span>
          </div>

          <div className="info-item">
            <label>{t("accountProfile.account.accountId")}:</label>
            <span>{displayAccountId}</span>
          </div>

          <div className="info-item">
            <label>{t("accountProfile.account.accountType")}:</label>
            <span>
              {isCorporateAccount
                ? t("accountProfile.account.typeBusiness")
                : t("accountProfile.account.typeIndividual")}
            </span>
          </div>

          <div className="info-item">
            <label>{t("accountProfile.account.owner")}:</label>
            <span>
              {ownerName}
              {ownerEmail ? ` (${ownerEmail})` : ""}
            </span>
          </div>

          <div className="info-item">
            <label>{t("accountProfile.account.status")}:</label>
            <span
              className={`status ${
                isActive && !isDelinquent ? "active" : "inactive"
              }`}
            >
              {!isActive
                ? t("accountProfile.account.statusInactive")
                : isDelinquent
                  ? t("accountProfile.account.statusDelinquent")
                  : t("accountProfile.account.statusActive")}
            </span>
          </div>

          <div className="info-item">
            <label>{t("accountProfile.account.memberSince")}:</label>
            <span>
              {createdAt ? formatDateOnly(createdAt, "short") : "N/A"}
            </span>
          </div>

          <div className="info-item">
            <label>{t("accountProfile.account.seats")}:</label>
            <span>
              {seatsUsed} {t("accountProfile.account.seatsOf")} {seatsTotal}
            </span>
          </div>

          <div className="info-item">
            <label>{t("accountProfile.account.plan")}:</label>
            <span>{getPlanName()}</span>
          </div>
        </div>
        <small className="info-note">
          {t("accountProfile.account.accountIdHelp")}
        </small>
      </div>

      <div className="common-form-group">
        <div className="btn-group">
          <button
            className="btn btn-ghost"
            onClick={() => setHeroContent("Member User Info")}
            title={t("accountProfile.account.manageMembers")}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />{" "}
            {t("accountProfile.account.manageMembers")}
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => setHeroContent("Account & Billing")}
            title={t("accountProfile.account.manageBilling")}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />{" "}
            {t("accountProfile.account.manageBilling")}
          </button>
        </div>
      </div>
    </>
  );

  const renderCompanySection = () => (
    <>
      {companySaveSuccess && (
        <div className="common-form-group">
          <p className="info-note" style={{ color: "#1a7f37" }}>
            {t("company.profile.saveSuccess")}
          </p>
        </div>
      )}
      {!isEditingCompany ? (
        <>
          <div className="common-form-group">
            <div className="account-info-grid">
              <div className="info-item">
                <label>{t("company.profile.companyName")}:</label>
                <span>{accountData?.name || notProvided}</span>
              </div>
              <div className="info-item">
                <label>{t("company.profile.descriptionLabel")}:</label>
                <span>{accountData?.description || notProvided}</span>
              </div>
              <div className="info-item">
                <label>{t("company.profile.industry")}:</label>
                <span>{accountData?.industry || notProvided}</span>
              </div>
              <div className="info-item">
                <label>{t("company.profile.employeeCount")}:</label>
                <span>
                  {accountData?.employeeCount != null
                    ? accountData.employeeCount
                    : notProvided}
                </span>
              </div>
              <div className="info-item">
                <label>{t("company.profile.companySize")}:</label>
                <span>{accountData?.companySize || notProvided}</span>
              </div>
            </div>
          </div>
          <div className="common-form-group">
            <p className="info-note">{t("company.profile.ownerOnlyNote")}</p>
            <div className="btn-group">
              <button
                className="btn btn-primary"
                onClick={beginEditCompany}
                title={t("company.profile.editButton")}
              >
                {t("company.profile.editButton")}
              </button>
            </div>
          </div>
        </>
      ) : (
        <form
          className="common-form-group company-profile-form"
          onSubmit={handleCompanySave}
        >
          {companySaveError && (
            <p className="error-text" style={{ color: "#c0392b" }}>
              {companySaveError}
            </p>
          )}
          <div className="form-group">
            <label htmlFor="company-name">
              {t("company.profile.companyName")}
            </label>
            <input
              type="text"
              id="company-name"
              name="name"
              value={companyForm.name}
              onChange={handleCompanyFieldChange}
              maxLength={255}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="company-description">
              {t("company.profile.descriptionLabel")}
            </label>
            <textarea
              id="company-description"
              name="description"
              rows={3}
              value={companyForm.description}
              onChange={handleCompanyFieldChange}
              placeholder={t("company.profile.descriptionPlaceholder")}
            />
          </div>
          <div className="form-group field-medium">
            <label htmlFor="company-industry">
              {t("company.profile.industry")}
            </label>
            <select
              id="company-industry"
              name="industry"
              value={companyForm.industry}
              onChange={handleCompanyFieldChange}
            >
              <option value="">
                {t("company.profile.industryPlaceholder")}
              </option>
              {INDUSTRY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group field-small">
            <label htmlFor="company-employee-count">
              {t("company.profile.employeeCount")}
            </label>
            <input
              type="number"
              id="company-employee-count"
              name="employeeCount"
              min="1"
              step="1"
              inputMode="numeric"
              value={companyForm.employeeCount}
              onChange={handleCompanyFieldChange}
              placeholder={t("company.profile.employeeCountPlaceholder")}
            />
            <small className="info-note">
              {t("company.profile.employeeCountHelp")}
            </small>
          </div>
          <div className="btn-group">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={companySaving}
            >
              {companySaving
                ? t("company.profile.saving")
                : t("company.profile.saveButton")}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={cancelEditCompany}
              disabled={companySaving}
            >
              {t("company.profile.cancelButton")}
            </button>
          </div>
        </form>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="common-container">
        <LoadingSpinner message={`${t("accountProfile.title")}...`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="common-container">
        <div className="account-error">
          <h2 className="main-title">{t("accountProfile.title")}</h2>
          <div className="error-message">
            <p>{error}</p>
            <button onClick={fetchAccountData} className="btn btn-ghost">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="common-container">
      <div className="common-header">
        <h1 className="main-title">{t("accountProfile.title")}</h1>
        <p className="common-subtitle">{t("accountProfile.subtitle")}</p>
      </div>

      {/* Account overview — always visible card */}
      <div className="common-section">
        <div className="common-section-content">{renderAccountSection()}</div>
      </div>

      {/* Company Profile — expand/collapse accordion */}
      {isCompanyOwner &&
        renderAccordion({
          id: "company",
          icon: faBuilding,
          iconClass: "user-update",
          title: t("company.profile.title"),
          subtitle: t("company.profile.description"),
          expanded: expandedSection === "company",
          onToggle: () => toggleSection("company"),
          children: renderCompanySection(),
        })}

      {/* Bottom spacing so the page has breathing room at the end */}
      <div className="account-profile-bottom-spacer" aria-hidden="true" />
    </div>
  );
}

export default AccountProfile;
